import React, { useState } from 'react';
import { ethers } from 'ethers';
import { useT } from '../../i18n';
import { AmountField } from './OpenPosition';
import {
  describePosition,
  num,
  toUnits,
  toInput,
  formatUsd,
  formatAmount,
  formatPct,
  formatRatio,
  ratioTone,
} from './cdpHelpers';

const ACTIONS = ['deposit', 'withdraw', 'mint', 'repay', 'close'];

const PositionCard = ({ position, market, account, actions }) => {
  const t = useT();
  const [action, setAction] = useState(null);
  const [input, setInput] = useState('');
  const v = describePosition(position, market);
  const { synth, collateral } = v;

  const walletCollateral = num(account.balances[collateral.address.toLowerCase()], collateral.decimals);
  const walletSynth = num(account.balances[synth.token.toLowerCase()]);
  const minDebtUsd = num(market.minDebtUsd);

  // collateral that can leave while the position stays at the minimum ratio
  const withdrawable =
    v.debt === 0
      ? v.collateralAmount
      : v.collateralPrice && v.debtUsd !== null
        ? Math.max(0, v.collateralAmount - (v.debtUsd * collateral.mcrBps) / 10000 / v.collateralPrice) * 0.999
        : 0;
  // maxMintable already accounts for the fee leaving the collateral
  const mintable = position.maxMintable ? num(position.maxMintable) * 0.999 : 0;
  const repayable = Math.min(v.debt, walletSynth);

  const limits = {
    deposit: { max: walletCollateral, decimals: collateral.decimals, unit: collateral.symbol },
    withdraw: { max: withdrawable, decimals: collateral.decimals, unit: collateral.symbol },
    mint: { max: mintable, decimals: 18, unit: synth.ticker },
    repay: { max: repayable, decimals: 18, unit: synth.ticker },
  };
  const limit = limits[action];
  const units = limit ? toUnits(input, limit.decimals) : null;

  // full repay / full withdraw use the on-chain amount so no dust is left behind
  const exactMax = () => {
    if (action === 'repay' && repayable === v.debt) return ethers.utils.formatUnits(position.debt, 18);
    if (action === 'withdraw' && v.debt === 0) return ethers.utils.formatUnits(position.collateralAmount, collateral.decimals);
    return null;
  };

  let problem = null;
  if (action === 'close') {
    if (walletSynth < v.debt) problem = t('cynths.closeNeedsSynth', { amount: formatAmount(v.debt, 6), ticker: synth.ticker });
  } else if (limit) {
    const amount = parseFloat(input) || 0;
    if (!units) problem = t('cynths.invalidAmount');
    else if (amount > limit.max * 1.000001) problem = t('cynths.aboveMax', { max: formatAmount(limit.max, 6) });
    else if (action === 'repay' && v.price && amount < v.debt && (v.debt - amount) * v.price < minDebtUsd) {
      problem = t('cynths.repayDust', { min: formatUsd(minDebtUsd) });
    } else if (action === 'mint' && v.price && (v.debt + amount) * v.price < minDebtUsd) {
      problem = t('cynths.minDebt', { min: formatUsd(minDebtUsd) });
    }
  }

  const execute = async () => {
    const id = position.id;
    if (action === 'deposit') await actions.depositCollateral(id, collateral.address, units);
    if (action === 'withdraw') await actions.withdrawCollateral(id, units);
    if (action === 'mint') await actions.mintSynth(id, units);
    if (action === 'repay') await actions.repay(id, synth.token, units);
    if (action === 'close') await actions.closePosition(id, synth.token, position.debt);
    setInput('');
    setAction(null);
  };

  return (
    <div className="lb-tile p-4 sm:p-5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="lb-chip notranslate">#{position.id}</span>
          <span className="notranslate font-bold text-white">{synth.ticker}</span>
          <span className="text-xs text-white/45">
            {t('cynths.backedBy')} <span className="notranslate">{collateral.symbol}</span>
          </span>
        </div>
        {position.liquidatable ? (
          <span className="lb-chip border-rose-400/40 text-rose-300">{t('cynths.liquidatable')}</span>
        ) : (
          v.ratio !== null && (
            <span className={`notranslate lb-num text-sm font-bold ${ratioTone(v.ratio, collateral.mcrBps, collateral.liquidationRatioBps)}`}>
              {formatRatio(v.ratio)}
            </span>
          )
        )}
      </div>

      <div className="mt-4 grid grid-cols-2 gap-3 text-sm md:grid-cols-4">
        <div>
          <p className="lb-label">{t('cynths.collateral')}</p>
          <p className="notranslate lb-num text-white">
            {formatAmount(v.collateralAmount)} {collateral.symbol}
          </p>
          <p className="notranslate text-xs text-white/45">{formatUsd(v.collateralUsd)}</p>
        </div>
        <div>
          <p className="lb-label">{t('cynths.debt')}</p>
          <p className="notranslate lb-num text-white">
            {formatAmount(v.debt, 6)} {synth.ticker}
          </p>
          <p className="notranslate text-xs text-white/45">{formatUsd(v.debtUsd)}</p>
        </div>
        <div>
          <p className="lb-label">{t('cynths.minRatio')}</p>
          <p className="notranslate lb-num text-white">{formatPct(collateral.mcrBps)}</p>
          <p className="text-xs text-white/45">
            {t('cynths.liqShort')} {formatPct(collateral.liquidationRatioBps)}
          </p>
        </div>
        <div>
          <p className="lb-label">{t('cynths.liquidationPrice', { ticker: synth.ticker })}</p>
          <p className="notranslate lb-num text-white">{v.liquidationPrice ? formatUsd(v.liquidationPrice) : '—'}</p>
          <p className="notranslate text-xs text-white/45">
            {t('cynths.now')} {formatUsd(v.price)}
          </p>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        {ACTIONS.map((a) => (
          <button
            key={a}
            type="button"
            onClick={() => {
              setAction(action === a ? null : a);
              setInput('');
            }}
            className={`lb-btn lb-btn-sm ${action === a ? 'lb-btn-primary' : a === 'close' ? 'lb-btn-danger' : 'lb-btn-outline'}`}
          >
            {t(`cynths.action.${a}`)}
          </button>
        ))}
      </div>

      {action && (
        <div className="mt-4 space-y-3 border-t border-white/5 pt-4">
          <p className="text-sm text-white/60">{t(`cynths.actionHint.${action}`)}</p>
          {limit && (
            <AmountField
              label={t(`cynths.action.${action}`)}
              balanceLabel={`${t('cynths.max')}: ${formatAmount(limit.max, 6)} ${limit.unit}`}
              value={input}
              onChange={setInput}
              onMax={() => setInput(exactMax() ?? toInput(limit.max, 8))}
              suffix={limit.unit}
            />
          )}
          {problem && (input !== '' || action === 'close') && <p className="text-sm text-amber-300">{problem}</p>}
          <button className="lb-btn lb-btn-primary w-full" disabled={!!problem} onClick={execute}>
            {t(`cynths.action.${action}`)}
          </button>
        </div>
      )}
    </div>
  );
};

const Positions = ({ market, account, actions }) => {
  const t = useT();
  if (!account) {
    return <p className="text-sm text-white/55">{t('cynths.connectFirst')}</p>;
  }
  if (!account.positions.length) {
    return <p className="text-sm text-white/55">{t('cynths.noPositions')}</p>;
  }
  return (
    <div className="space-y-4">
      {account.positions.map((p) => (
        <PositionCard key={p.id} position={p} market={market} account={account} actions={actions} />
      ))}
    </div>
  );
};

export default Positions;
